import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook for loading language-specific documentation images
 * 
 * This hook dynamically loads images based on the current language.
 * Images should be organized in language subfolders:
 *   - /en/image.png (English - fallback)
 *   - /pt/image.png (Portuguese)
 *   - /sq/image.png (Albanian)
 * 
 * @param {string} folder - The image folder name (e.g., 'sync-img', 'analytics-img')
 * @param {string} imageName - The image file name (e.g., 'sync-1.png')
 * @param {string} category - The category: 'survey' or 'settings' (default: 'survey')
 * @returns {string} The image source path for the current language
 * 
 * @example
 * // In your component:
 * const image01 = useDocImage('sync-img', 'sync-1.png');
 * const image02 = useDocImage('roles-img', 'image001.png', 'settings');
 * 
 * return <img src={image01} alt="..." />;
 */
export function useDocImage(folder, imageName, category = 'survey') {
  const { i18n } = useTranslation();
  const currentLang = i18n.language?.substring(0, 2) || 'en';

  const imageSrc = useMemo(() => {
    // Supported languages
    const supportedLangs = ['en', 'pt', 'sq'];
    const lang = supportedLangs.includes(currentLang) ? currentLang : 'en';

    try {
      // Try to load language-specific image
      if (category === 'settings') {
        return require(`../assetes/images/settings/${folder}/${lang}/${imageName}`);
      } else {
        return require(`../assetes/images/survey/${folder}/${lang}/${imageName}`);
      }
    } catch (e) {
      // Fallback to English if language-specific image doesn't exist
      try {
        if (category === 'settings') {
          return require(`../assetes/images/settings/${folder}/en/${imageName}`);
        } else {
          return require(`../assetes/images/survey/${folder}/en/${imageName}`);
        }
      } catch (e2) {
        // Final fallback: try without language folder (for backward compatibility during migration)
        try {
          if (category === 'settings') {
            return require(`../assetes/images/settings/${folder}/${imageName}`);
          } else {
            return require(`../assetes/images/survey/${folder}/${imageName}`);
          }
        } catch (e3) {
          console.warn(`Image not found: ${folder}/${imageName}`);
          return '';
        }
      }
    }
  }, [folder, imageName, category, currentLang]);

  return imageSrc;
}

/**
 * Helper function to get multiple images at once
 * 
 * @param {string} folder - The image folder name
 * @param {string[]} imageNames - Array of image file names
 * @param {string} category - The category: 'survey' or 'settings'
 * @returns {Object} Object with image names as keys and sources as values
 * 
 * @example
 * const images = useDocImages('sync-img', ['sync-1.png', 'sync-2.png', 'sync-3.png']);
 * return <img src={images['sync-1.png']} alt="..." />;
 */
export function useDocImages(folder, imageNames, category = 'survey') {
  const { i18n } = useTranslation();
  const currentLang = i18n.language?.substring(0, 2) || 'en';

  const images = useMemo(() => {
    const supportedLangs = ['en', 'pt', 'sq'];
    const lang = supportedLangs.includes(currentLang) ? currentLang : 'en';
    
    const result = {};
    
    imageNames.forEach(imageName => {
      try {
        if (category === 'settings') {
          result[imageName] = require(`../assetes/images/settings/${folder}/${lang}/${imageName}`);
        } else {
          result[imageName] = require(`../assetes/images/survey/${folder}/${lang}/${imageName}`);
        }
      } catch (e) {
        try {
          // Fallback to English
          if (category === 'settings') {
            result[imageName] = require(`../assetes/images/settings/${folder}/en/${imageName}`);
          } else {
            result[imageName] = require(`../assetes/images/survey/${folder}/en/${imageName}`);
          }
        } catch (e2) {
          // Backward compatibility fallback
          try {
            if (category === 'settings') {
              result[imageName] = require(`../assetes/images/settings/${folder}/${imageName}`);
            } else {
              result[imageName] = require(`../assetes/images/survey/${folder}/${imageName}`);
            }
          } catch (e3) {
            console.warn(`Image not found: ${folder}/${imageName}`);
            result[imageName] = '';
          }
        }
      }
    });
    
    return result;
  }, [folder, imageNames, category, currentLang]);

  return images;
}

export default useDocImage;

